import React, { useState } from 'react';
import { superAdminApi } from '../../services/superAdminApi';

const DomainStatus = ({ domain }) => {
  const [loading, setLoading] = useState(false);
  const [sslInfo, setSslInfo] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  const loadSslInfo = async () => {
    if (sslInfo || !domain.ssl_enabled) return;
    
    try {
      setLoading(true);
      const response = await superAdminApi.getDomainSslInfo(domain.id);
      setSslInfo(response.data);
    } catch (error) {
      console.error('Failed to load SSL info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusClick = () => {
    if (domain.ssl_enabled) {
      setShowDetails(!showDetails);
      if (!showDetails) {
        loadSslInfo();
      }
    }
  };

  const getSslStatus = () => {
    if (!domain.ssl_enabled) {
      return {
        icon: 'unlock',
        class: 'ssl-disabled',
        text: 'No SSL',
        color: '#dc3545'
      };
    }

    const expiresAt = domain.ssl_expires_at ? new Date(domain.ssl_expires_at) : null;
    const now = new Date();
    const daysUntilExpiry = expiresAt ? Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24)) : null;

    if (daysUntilExpiry !== null) {
      if (daysUntilExpiry < 0) {
        return {
          icon: 'exclamation-triangle',
          class: 'ssl-expired',
          text: 'Expired',
          color: '#dc3545'
        };
      } else if (daysUntilExpiry <= 30) {
        return {
          icon: 'exclamation-triangle',
          class: 'ssl-expiring',
          text: `${daysUntilExpiry}d left`,
          color: '#ffc107'
        };
      }
    }

    return {
      icon: 'lock',
      class: 'ssl-active',
      text: 'SSL Active',
      color: '#28a745'
    };
  };

  const status = getSslStatus();

  return (
    <div className="domain-ssl-status">
      <div
        className={`ssl-indicator ${status.class}`}
        onClick={handleStatusClick}
        style={{ cursor: domain.ssl_enabled ? 'pointer' : 'default' }}
        title={domain.ssl_enabled ? 'Click for SSL details' : 'SSL not enabled'}
      >
        <i 
          className={`fas fa-${status.icon}`}
          style={{ color: status.color }}
        ></i>
        <span className="ssl-text">{status.text}</span>
      </div>

      {showDetails && domain.ssl_enabled && (
        <div className="ssl-details-popup">
          {loading ? (
            <div className="loading-spinner">
              <i className="fas fa-spinner fa-spin"></i>
              Loading SSL details...
            </div>
          ) : sslInfo ? (
            <div className="ssl-details">
              <div className="ssl-detail-item">
                <label>Status:</label>
                <span className={sslInfo.enabled ? 'text-success' : 'text-danger'}>
                  {sslInfo.enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              
              {sslInfo.expires_at && (
                <div className="ssl-detail-item">
                  <label>Expires:</label>
                  <span className={sslInfo.is_expiring ? 'text-warning' : 'text-success'}>
                    {new Date(sslInfo.expires_at).toLocaleDateString()}
                  </span>
                </div>
              )}
              
              {sslInfo.days_until_expiry !== null && (
                <div className="ssl-detail-item">
                  <label>Days Left:</label>
                  <span className={
                    sslInfo.days_until_expiry < 0 ? 'text-danger' :
                    sslInfo.days_until_expiry <= 30 ? 'text-warning' : 'text-success'
                  }>
                    {sslInfo.days_until_expiry < 0 ? 'Expired' : `${sslInfo.days_until_expiry} days`}
                  </span>
                </div>
              )}
              
              <div className="ssl-actions">
                {sslInfo.is_expiring && (
                  <button
                    className="btn-sm btn-warning"
                    title="Renew SSL Certificate"
                  >
                    <i className="fas fa-sync"></i>
                    Renew
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="ssl-error">
              Failed to load SSL details
            </div>
          )}
          
          <button
            className="close-details"
            onClick={() => setShowDetails(false)}
            title="Close details"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
      )}
    </div>
  );
};

export default DomainStatus;